import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../../api/auth";
import { base } from "../../api/base";
import { loadRememberedLogin, saveRememberedLogin } from "../../utils/loginRemember";
import { consumeLoginRedirect, persistLoginRedirect } from "../../utils/loginRedirect";
import { message } from "antd";
import { ThemeContext } from "styled-components";
import { useLocale } from 'contexts/LocaleContext';
import { useIntl } from 'react-intl';
import SEO, { SEOConfigs } from 'components/SEO';
import SimpleHeader from 'components/headers/simple';
import FooterSection from 'pages/Home/components/FooterSection';

import { PageContainer } from './styles';
import { RightSection } from './components/RightSection';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberPassword, setRememberPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const theme = React.useContext(ThemeContext);
  const { locale, changeLocale } = useLocale();
  const intl = useIntl();
  const [languages, setLanguages] = useState([]);

  // 获取支持的语言列表
  useEffect(() => {
    const fetchLanguages = async () => {
      const result = await base.getEnabledLanguages();
      if (result.success) {
        const sortedLanguages = result.data.sort((a, b) => b.usageCount - a.usageCount);
        setLanguages(sortedLanguages);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (redirect) {
      persistLoginRedirect(redirect);
    }
  }, [searchParams]);

  useEffect(() => {
    const saved = loadRememberedLogin();
    if (saved.remember) {
      setEmail(saved.email);
      setPassword(saved.password);
      setRememberPassword(true);
    }
  }, []);

  const handleSubmit = async (e, captcha) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await auth.login({
        email,
        password,
        captchaId: captcha?.captchaId,
        captchaCode: captcha?.captchaCode,
      });
      if (result.success) {
        saveRememberedLogin(email, password, rememberPassword);
        message.success("登录成功");
        navigate(consumeLoginRedirect('/'));
      } else if (!result.isUserDisabled && !result.isIpBlocked) {
        setError(result.message || "登录失败");
        captcha?.refreshCaptcha?.();
      }
    } catch (error) {
      if (!error?.isUserDisabled && !error?.isIpBlocked) {
        setError(error.response?.data?.message || '登录失败，请稍后重试');
        captcha?.refreshCaptcha?.();
      }
    } finally {
      setLoading(false);
    }
  };

  // 处理移动端虚拟键盘
  useEffect(() => {
    const handleResize = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return (
    <>
      <SEO {...SEOConfigs.login}>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </SEO>
      <SimpleHeader />
      <PageContainer>
        <RightSection 
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          rememberPassword={rememberPassword}
          setRememberPassword={setRememberPassword}
          error={error}
          loading={loading}
          handleSubmit={handleSubmit}
          intl={intl}
          locale={locale}
        />
      </PageContainer>
      <FooterSection />
    </>
  );
};

export default LoginPage;
